import * as React from "react";
import { ArrowUp, Square } from "lucide-react";
import { Button, type buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";

type PromptInputStatus = "submitted" | "streaming" | "ready" | "error";

function PromptInput({ className, ...props }: React.ComponentProps<"form">) {
  return (
    <form
      data-slot="prompt-input"
      className={cn("rounded-md border bg-white p-2", className)}
      {...props}
    />
  );
}

const PromptInputTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(function PromptInputTextarea({ className, onInput, ...props }, ref) {
  const localRef = React.useRef<HTMLTextAreaElement | null>(null);

  const resize = React.useCallback((node: HTMLTextAreaElement | null) => {
    if (!node) return;
    node.style.height = "0px";
    node.style.height = `${Math.min(node.scrollHeight, 160)}px`;
  }, []);

  return (
    <textarea
      ref={(node) => {
        localRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
        resize(node);
      }}
      data-slot="prompt-input-textarea"
      rows={1}
      onInput={(event) => {
        resize(event.currentTarget);
        onInput?.(event);
      }}
      className={cn(
        "max-h-40 min-h-10 w-full resize-none border-0 bg-transparent px-2 py-2 text-sm leading-6 outline-none placeholder:text-[#8A8A8A] disabled:cursor-not-allowed disabled:opacity-60",
        className,
      )}
      {...props}
    />
  );
});

function PromptInputToolbar({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="prompt-input-toolbar"
      className={cn(
        "flex items-center justify-between gap-2 px-1 pt-1",
        className,
      )}
      {...props}
    />
  );
}

function PromptInputTools({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="prompt-input-tools"
      className={cn("flex min-w-0 flex-wrap items-center gap-1.5", className)}
      {...props}
    />
  );
}

type PromptInputButtonProps = React.ComponentProps<typeof Button> &
  VariantProps<typeof buttonVariants>;

function PromptInputButton({
  className,
  variant = "ghost",
  size = "sm",
  ...props
}: PromptInputButtonProps) {
  return (
    <Button
      type="button"
      data-slot="prompt-input-button"
      variant={variant}
      size={size}
      className={cn("h-8 rounded-md px-2 text-xs", className)}
      {...props}
    />
  );
}

function PromptInputSubmit({
  status = "ready",
  className,
  ...props
}: React.ComponentProps<typeof Button> & { status?: PromptInputStatus }) {
  const busy = status === "submitted" || status === "streaming";
  return (
    <Button
      type="submit"
      size="icon"
      data-slot="prompt-input-submit"
      className={cn(
        "h-8 w-8 rounded-md bg-[#FF5E00] text-white hover:bg-[#ff8138]",
        className,
      )}
      {...props}
    >
      {busy ? (
        <Square className="h-3.5 w-3.5 fill-current" />
      ) : (
        <ArrowUp className="h-4 w-4" />
      )}
      <span className="sr-only">{busy ? "Stop response" : "Send message"}</span>
    </Button>
  );
}

export {
  PromptInput,
  PromptInputButton,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
};
